import { useState, useEffect } from 'react';
import { availabilityService } from '../services/api';
import { formatApptDate } from '../utils/dateFormatters';
import './Availability.css';

const DAYS = [
  { dow: 1, label: 'Monday' }, { dow: 2, label: 'Tuesday' }, { dow: 3, label: 'Wednesday' },
  { dow: 4, label: 'Thursday' }, { dow: 5, label: 'Friday' }, { dow: 6, label: 'Saturday' }, { dow: 0, label: 'Sunday' }
];
const EMPTY_DAY = { enabled: false, start: '09:00', end: '17:00' };

export default function Availability() {
  const [hours, setHours] = useState(() => Object.fromEntries(DAYS.map((d) => [d.dow, { ...EMPTY_DAY }])));
  const [exceptions, setExceptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [exForm, setExForm] = useState({ date: '', type: 'blocked', start_time: '', end_time: '', reason: '' });

  async function loadAll() {
    const [wh, ex] = await Promise.all([availabilityService.getWorkingHours(), availabilityService.getExceptions()]);
    if (wh.success) {
      const next = Object.fromEntries(DAYS.map((d) => [d.dow, { ...EMPTY_DAY }]));
      for (const row of wh.working_hours || []) {
        if (!next[row.day_of_week]?.enabled) {
          next[row.day_of_week] = { enabled: !!row.is_active, start: row.start_time, end: row.end_time };
        }
      }
      setHours(next);
    }
    if (ex.success) setExceptions(ex.exceptions || []);
  }
  useEffect(() => { loadAll(); }, []);

  function setDay(dow, patch) { setHours((prev) => ({ ...prev, [dow]: { ...prev[dow], ...patch } })); }

  async function saveHours() {
    setSaving(true); setSavedMsg('');
    const payload = DAYS.filter((d) => hours[d.dow].enabled)
      .map((d) => ({ day_of_week: d.dow, start_time: hours[d.dow].start, end_time: hours[d.dow].end, is_active: true }));
    const res = await availabilityService.updateWorkingHours(payload);
    setSaving(false);
    setSavedMsg(res.success ? 'Saved' : (res.error || 'Failed to save'));
    setTimeout(() => setSavedMsg(''), 2500);
  }

  async function addException(e) {
    e.preventDefault();
    if (!exForm.date) return;
    const res = await availabilityService.addException({
      date: exForm.date, type: exForm.type,
      start_time: exForm.start_time || null, end_time: exForm.end_time || null, reason: exForm.reason || null
    });
    if (res.success) { setExForm({ date: '', type: 'blocked', start_time: '', end_time: '', reason: '' }); loadAll(); }
  }

  async function delException(id) { await availabilityService.deleteException(id); loadAll(); }

  return (
    <div className="availability-page">
      <div className="page-header"><h1 className="page-title">My Availability</h1></div>

      <section className="card avail-section">
        <div className="card-header"><h2 className="card-title">Weekly hours</h2>
          <div className="flex items-center gap-sm">
            {savedMsg && <span className="text-success">{savedMsg}</span>}
            <button className="btn btn-primary btn-sm" onClick={saveHours} disabled={saving}>{saving ? 'Saving…' : 'Save hours'}</button>
          </div>
        </div>
        <div className="hours-list">
          {DAYS.map((d) => (
            <div key={d.dow} className={`hours-row ${hours[d.dow].enabled ? '' : 'off'}`}>
              <label className="checkbox-label hours-day">
                <input type="checkbox" checked={hours[d.dow].enabled} onChange={(e) => setDay(d.dow, { enabled: e.target.checked })} />
                {d.label}
              </label>
              {hours[d.dow].enabled ? (
                <div className="hours-times">
                  <input type="time" value={hours[d.dow].start} onChange={(e) => setDay(d.dow, { start: e.target.value })} />
                  <span>to</span>
                  <input type="time" value={hours[d.dow].end} onChange={(e) => setDay(d.dow, { end: e.target.value })} />
                </div>
              ) : <span className="text-muted">Unavailable</span>}
            </div>
          ))}
        </div>
      </section>

      <section className="card avail-section">
        <div className="card-header"><h2 className="card-title">Time off &amp; one-offs</h2></div>
        <form className="exception-form" onSubmit={addException}>
          <input type="date" value={exForm.date} onChange={(e) => setExForm({ ...exForm, date: e.target.value })} required />
          <select value={exForm.type} onChange={(e) => setExForm({ ...exForm, type: e.target.value })}>
            <option value="blocked">Block out</option>
            <option value="extra">Extra hours</option>
          </select>
          <input type="time" value={exForm.start_time} onChange={(e) => setExForm({ ...exForm, start_time: e.target.value })} placeholder="From" title="Leave blank to block the whole day" />
          <input type="time" value={exForm.end_time} onChange={(e) => setExForm({ ...exForm, end_time: e.target.value })} placeholder="To" />
          <input type="text" value={exForm.reason} onChange={(e) => setExForm({ ...exForm, reason: e.target.value })} placeholder="Reason (optional)" />
          <button type="submit" className="btn btn-primary btn-sm">Add</button>
        </form>

        {exceptions.length === 0 ? <p className="text-muted">No exceptions set.</p> : (
          <ul className="exception-list">
            {exceptions.map((ex) => (
              <li key={ex.id}>
                <span className={`chip ${ex.type === 'blocked' ? '' : 'chip-extra'}`}>{ex.type === 'blocked' ? 'Blocked' : 'Extra'}</span>
                <strong>{formatApptDate(ex.date + 'T00:00:00')}</strong>
                <span className="text-muted">
                  {ex.start_time ? ` ${ex.start_time}–${ex.end_time}` : ' all day'}{ex.reason ? ` · ${ex.reason}` : ''}
                </span>
                <button className="btn btn-sm btn-outline" onClick={() => delException(ex.id)}>Remove</button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
